import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import KapeiLogo from "@/components/KapeiLogo";
import { Lock, CheckCircle2, AlertTriangle } from "lucide-react";

export default function PublicPaymentPage() {
  const { code } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    setErr("");
    try {
      const resp = await api.get(`/payments/public/${encodeURIComponent(code)}`);
      setData(resp.data);
    } catch (e) {
      setErr(formatApiError(e?.response?.data?.detail) || "No se pudo cargar el pago.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code]);

  return (
    <div className="min-h-screen bg-slate-50 px-4 py-6">
      <div className="mx-auto max-w-md">
        <div className="mb-6 flex justify-center">
          <KapeiLogo />
        </div>

        {loading && (
          <div className="rounded-3xl border-2 border-slate-200 bg-white p-6 text-center font-bold text-slate-600">
            Cargando pago…
          </div>
        )}

        {!loading && err && (
          <div className="rounded-3xl border-2 border-red-200 bg-red-50 p-6 text-red-900">
            <div className="flex items-center gap-2 text-xl font-black">
              <AlertTriangle className="h-6 w-6" />
              No se pudo cargar el pago
            </div>
            <p className="mt-2 text-sm font-semibold">{err}</p>
            <Link to="/" className="mt-4 block rounded-2xl bg-[#4d7c0f] px-5 py-3 text-center font-black text-white">
              Ir a KAPEI
            </Link>
          </div>
        )}

        {!loading && data && data.payment_status === "paid" && (
          <div className="rounded-3xl border-2 border-emerald-300 bg-emerald-50 p-6 text-emerald-950">
            <div className="flex items-center gap-2 text-2xl font-black">
              <CheckCircle2 className="h-7 w-7" />
              Pago recibido
            </div>
            <p className="mt-2 text-sm font-bold">
              El pedido {data.code} ya aparece como pagado correctamente.
            </p>
            {data.payment_amount_eur != null && (
              <div className="mt-4 rounded-2xl bg-white/80 p-4">
                <div className="text-xs font-black uppercase tracking-widest text-emerald-700">Importe</div>
                <div className="text-3xl font-black">{Number(data.payment_amount_eur || 0).toFixed(2)} €</div>
              </div>
            )}
          </div>
        )}

        {!loading && data && data.payment_status !== "paid" && data.payment_checkout_url && (
          <div className="rounded-3xl border-2 border-emerald-300 bg-emerald-50 p-6 text-emerald-950">
            <div className="flex items-center gap-2 text-2xl font-black">
              <Lock className="h-7 w-7" />
              Pago seguro KAPEI
            </div>

            <div className="mt-4 rounded-2xl bg-white/80 p-4">
              <div className="text-xs font-black uppercase tracking-widest text-emerald-700">Pedido</div>
              <div className="font-mono text-3xl font-black text-[#4d7c0f]">{data.code}</div>
              {data.payment_description && (
                <p className="mt-2 text-sm font-semibold text-slate-700">{data.payment_description}</p>
              )}
              {data.payment_note && (
                <div className="mt-3 rounded-xl bg-emerald-50 p-3 text-sm font-black text-emerald-950">
                  {data.payment_note}
                </div>
              )}
            </div>

            <div className="mt-4 rounded-2xl bg-white/80 p-4">
              <div className="text-xs font-black uppercase tracking-widest text-emerald-700">Importe pendiente</div>
              <div className="text-4xl font-black">{Number(data.payment_amount_eur || 0).toFixed(2)} €</div>
            </div>

            <p className="mt-4 text-sm font-bold">
              Puede pagar de forma segura con tarjeta. KAPEI no almacena los datos de su tarjeta.
            </p>

            <a
              href={data.payment_checkout_url}
              className="mt-5 block rounded-2xl bg-[#4d7c0f] px-5 py-4 text-center text-lg font-black text-white shadow-lg hover:bg-[#3f6212]"
            >
              PAGAR AHORA
            </a>

            <p className="mt-3 text-xs font-semibold text-emerald-800">
              Pago procesado mediante una plataforma de pago segura.
            </p>
          </div>
        )}

        {!loading && data && data.payment_status !== "paid" && !data.payment_checkout_url && (
          <div className="rounded-3xl border-2 border-amber-300 bg-amber-50 p-6 text-amber-950">
            <div className="text-2xl font-black">No hay pago pendiente</div>
            <p className="mt-2 text-sm font-semibold">
              El pedido {data.code} no tiene ningún pago online pendiente en este momento.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
